import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, mergeMap, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { Account } from '../../models/account/account.model';
import { LoyaltyHistoryRecord } from '../../models/account/loyalty-history-record.model';
import { LoyaltyMembership } from '../../models/account/loyalty-membership.model';
import { LoyaltyNotification } from '../../models/account/loyalty-notification.model';
import { LoyaltySignupData } from '../../models/account/loyalty-signup-data.model';
import { LoyaltyBadge } from '../../models/core/loyalty-badge.model';
import { AuditService } from '../core/audit.service';
import { HypermediaService } from '../core/hypermedia.service';
import { AccountService } from './account.service';
import { differenceInMilliseconds } from 'date-fns';
import { Utils } from '../../utils/utils';
import { LoyaltySubscription, SubscriptionPayment } from 'ngx-web-api/lib/models/core/loyalty-subscription.model';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class AccountLoyaltyService {
  private readonly BASE_PATH = '/ws/integrated/v1/ordering/account/loyalty';

  private historyCache = new Map<string, Observable<LoyaltyHistoryRecord[]>>();

  private loyaltyMembership: BehaviorSubject<LoyaltyMembership> = new BehaviorSubject<LoyaltyMembership>(null);
  public loyaltyMembership$: Observable<LoyaltyMembership> = this.accountService.account$.pipe(
    filter((account: Account) => account?.isInstantiated && account.signUpForLoyalty),
    distinctUntilChanged((prev, curr) => prev?.id === curr?.id && prev?.updatedAt === curr?.updatedAt),
    switchMap((account: Account) => this.hypermedia.get(account.links, 'memberships')),
    map((json: Object[]) => (json || []).map(v => new LoyaltyMembership().deserialize(v))[0]),
    tap((m: LoyaltyMembership) => this.loyaltyMembership.next(m)),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  private _loyaltySubscription: BehaviorSubject<LoyaltySubscription> = new BehaviorSubject<LoyaltySubscription>(null);
  public loyaltySubscription$: Observable<LoyaltySubscription> = this._loyaltySubscription.asObservable();

  constructor(
    private http: HttpClient,
    private hypermedia: HypermediaService,
    private auditService: AuditService,
    private accountService: AccountService,
    private translateService: TranslateService
  ) {}

  public signupForLoyalty(data: LoyaltySignupData): Observable<Account> {
    return this.http.post(this.BASE_PATH, data.serialize()).pipe(
      tap(() => this.auditService.createAudit(() => ({ message: `User with cellphone ${data.cellphone} has signed up for loyalty` }))),
      mergeMap(() => this.accountService.fetchAccount())
    );
  }

  public getLoyaltyNotifications(email: string, token: string): Observable<LoyaltyNotification[]> {
    // We use encodeURIComponent here since we might have emails with characters that should be escaped and
    // angular escapes them (they have a bug here).
    const query = `Email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`;
    return this.http
      .get(`${this.BASE_PATH}/notifications?${query}`)
      .pipe(map((json: Object[]) => (json || []).map(v => new LoyaltyNotification().deserialize(v))));
  }

  public updateLoyaltyNotifications(membership: LoyaltyMembership, notifications: LoyaltyNotification[]) {
    const notificationsLink = this.hypermedia.findLink(membership.links, 'notifications');
    if (!notificationsLink) {
      const noLinkMessage = 'No notifications link has been found';
      this.auditService.createAudit(() => noLinkMessage);
      throw new Error(noLinkMessage);
    }
    return this.http
      .put(notificationsLink.href, notifications)
      .pipe(map((json: Object[]) => (json || []).map(v => new LoyaltyNotification().deserialize(v))));
  }

  public updateLoyaltyNotificationsViaToken(
    email: string,
    token: string,
    notifications: LoyaltyNotification[]
  ): Observable<LoyaltyNotification[]> {
    return this.http
      .post(`${this.BASE_PATH}/notifications`, {
        email,
        token,
        notifications: notifications.map(n => n.serialize()),
      })
      .pipe(
        map((json: Object) => json['notifications']),
        map((nots: Object[]) => (nots || []).map(n => new LoyaltyNotification().deserialize(n))),
        tap(() => this.auditService.createAudit(() => ({ message: 'Loyalty notifications have been updated', details: { notifications } })))
      );
  }

  public getLoyaltyMembership(): Observable<LoyaltyMembership> {
    return this.loyaltyMembership$.pipe(
      filter(membership => !!membership),
      take(1)
    );
  }

  public getLoyaltyMembershipHistory(m: LoyaltyMembership): Observable<LoyaltyHistoryRecord[]> {
    const key = m?.id || JSON.stringify(m?.links);
    if (!this.historyCache.has(key)) {
      const cached$ = this.hypermedia.get(m.links, 'history').pipe(
        map((json: Object[]) => (json || []).map(r => new LoyaltyHistoryRecord().deserialize(r))),
        map((records: LoyaltyHistoryRecord[]) =>
          records.sort((a, b) => differenceInMilliseconds(Utils.parseIsoDate(b.date), Utils.parseIsoDate(a.date)))
        ),
        shareReplay({ bufferSize: 1, refCount: false })
      );
      this.historyCache.set(key, cached$);
    }
    return this.historyCache.get(key);
  }

  public getLoyaltyMembershipBadges(membership: LoyaltyMembership): Observable<LoyaltyBadge[]> {
    return this.hypermedia
      .get(membership.links, 'badges')
      .pipe(map((json: Object[]) => (json || []).map(b => new LoyaltyBadge().deserialize(b))));
  }

  public getLoyaltyMembershipNotifications(membership: LoyaltyMembership): Observable<LoyaltyNotification[]> {
    return this.hypermedia
      .get(membership.links, 'notifications')
      .pipe(map((json: Object[]) => (json || []).map(n => new LoyaltyNotification().deserialize(n))));
  }

  public getThridPartyRewardBalance(): Observable<any> {
    return this.http.get(`${this.BASE_PATH}/balance`);
  }

  private subscriptionCache = new Map<string, Observable<LoyaltySubscription>>();

  public getAccountSubscription(membership: LoyaltyMembership, forceRefresh = false): Observable<LoyaltySubscription> {
    const key = membership.id || JSON.stringify(membership.links);

    if (forceRefresh) {
      this.subscriptionCache.delete(key);
    }

    if (!this.subscriptionCache.has(key)) {
      const request$ = this.hypermedia.get(membership.links, 'subscription').pipe(
        map((json: Object) => new LoyaltySubscription().deserialize(json)),
        tap((loyaltySub: LoyaltySubscription) => this._loyaltySubscription.next(loyaltySub)),
        shareReplay(1) // Cache the result for future subscribers
      );
      this.subscriptionCache.set(key, request$);
    }

    return this.subscriptionCache.get(key)!;
  }

  public placeSubscription(payment: SubscriptionPayment) {
    return this.http.post(`${this.BASE_PATH}/${payment.subscriptionName}/subscription/subscribe`, payment).pipe(
      switchMap(() =>
        this.loyaltyMembership$.pipe(
          take(1),
          switchMap(membership => this.getAccountSubscription(membership, true))
        )
      )
    );
  }

  public placeTrialSubscription(payment: SubscriptionPayment) {
    return this.http.post(`${this.BASE_PATH}/${payment.subscriptionName}/subscription/trial`, payment).pipe(
      switchMap(() =>
        this.loyaltyMembership$.pipe(
          take(1),
          switchMap(membership => this.getAccountSubscription(membership, true))
        )
      )
    );
  }

  public placeReactivate(): Observable<any> {
    return this.loyaltyMembership$.pipe(
      take(1),
      switchMap(loyaltyMembership => {
        if (!loyaltyMembership.loyaltyPlan) {
          this.auditService.createAudit(() => ({
            message: 'No loyalty membership found when trying to reactivate subscription',
          }));
          return throwError(() => new Error(this.translateService.instant('modal.cancel_subscription.no_loyalty')));
        }
        return this.http
          .post(`${this.BASE_PATH}/${loyaltyMembership.loyaltyPlan}/subscription/cancel?undoCancellation=true`, {})
          .pipe(switchMap(() => this.getAccountSubscription(loyaltyMembership, true)));
      }),
      catchError(err => {
        this.auditService.createAudit(() => ({
          message: 'Failed to reactivate subscription:',
          details: { error: err },
        }));
        return throwError(() => err);
      })
    );
  }

  public cancelSubscription(): Observable<any> {
    return this.loyaltyMembership$.pipe(
      take(1),
      switchMap(loyaltyMembership => {
        if (!loyaltyMembership.loyaltyPlan) {
          this.auditService.createAudit(() => ({
            message: 'No loyalty membership found when trying to cancel subscription',
          }));
          return throwError(() => new Error(this.translateService.instant('modal.cancel_subscription.no_loyalty')));
        }

        return this.http
          .post(`${this.BASE_PATH}/${loyaltyMembership.loyaltyPlan}/subscription/cancel`, {})
          .pipe(switchMap(() => this.getAccountSubscription(loyaltyMembership, true)));
      }),
      catchError(err => {
        this.auditService.createAudit(() => ({
          message: 'Failed to cancel subscription:',
          details: { error: err },
        }));
        return throwError(() => err);
      })
    );
  }
}
